<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">

            <!-- Pasek statusu -->
            <div class="mb-3 flex items-center justify-between">
              <div class="text-sm text-gray-600">
                <template v-if="isLoading">Ładowanie okresów… ({{ periods.length }} znalezionych)</template>
                <template v-else>Załadowane okresy: {{ periods.length }}</template>
              </div>
              <div class="flex gap-2">
                <button type="button" @click="loadFirstPage()" :disabled="isLoading"
                        class="px-3 py-1.5 rounded-md bg-slate-100 text-sm hover:bg-slate-200 disabled:opacity-50">
                  Odśwież
                </button>
                <button type="button" @click="loadMore()" :disabled="!canLoadMore || isLoading"
                        class="px-3 py-1.5 rounded-md bg-slate-100 text-sm hover:bg-slate-200 disabled:opacity-50">
                  Załaduj więcej
                </button>
              </div>
            </div>

            <div class="overflow-auto h-96">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Okres</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Ilość faktur</th>
                    <th class="relative py-3.5 pl-3 pr-4 sm:pr-3"></th>
                  </tr>
                </thead>
                <tbody class="bg-white">
                  <tr v-for="(row, idx) in periods" :key="row.key" :class="idx % 2 ? 'bg-gray-50' : ''">
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                      <div class="flex flex-col">
                        <span>{{ row.key }}</span>
                        <span v-if="row.parsed" class="text-xs text-gray-500">{{ row.parsed.startISO }} → {{ row.parsed.endISO }}</span>
                      </div>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span v-if="row.meta.loading">…</span>
                      <span v-else-if="row.meta.count !== null">{{ row.meta.count }}</span>
                      <span v-else>—</span>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <input v-model="row.selected" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600" />
                    </td>
                  </tr>

                  <tr v-if="!periods.length">
                    <td colspan="3" class="py-6 text-center text-sm text-gray-500">Załaduj pierwszą paczkę okresów.</td>
                  </tr>
                </tbody>
              </table>

              <!-- Strażnik do infinite loading -->
              <div ref="sentinel" class="h-6 w-full"></div>
            </div>

            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse flex-wrap gap-2">
              <button type="button"
                      :disabled="preventSpam || !hasAnySelected"
                      :class="['items-center gap-x-1.5 transition ease-in-out duration-300 bg-green-600 text-base font-medium hover:bg-green-700 focus:ring-green-500',
                               'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50']"
                      @click.prevent="generateXLSXStatement()">
                <TableCellsIcon class="-ml-0.5 h-6 w-6" aria-hidden="true" />
                <span v-if="!preventSpam">Generuj XLSX</span>
                <span v-else>Przetwarzanie…</span>
              </button>
              <button type="button"
                      :disabled="preventSpam || !hasAnySelected"
                      :class="['items-center gap-x-1.5 transition ease-in-out duration-300 bg-blue-600 text-base font-medium hover:bg-blue-700 focus:ring-blue-500',
                               'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50']"
                      @click.prevent="generateCSVStatement()">
                <ChartBarSquareIcon class="-ml-0.5 h-6 w-6" aria-hidden="true" />
                <span v-if="!preventSpam">Generuj CSV</span>
                <span v-else>Przetwarzanie…</span>
              </button>
              <button type="button"
                      :disabled="preventSpam || !hasAnySelected"
                      :class="['items-center gap-x-1.5 transition ease-in-out duration-300 bg-blue-600 text-base font-medium hover:bg-blue-700 focus:ring-blue-500',
                               'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50']"
                      @click.prevent="generateStatement()">
                <DocumentDuplicateIcon class="-ml-0.5 h-6 w-6" aria-hidden="true" />
                <span v-if="!preventSpam">Generuj PDF</span>
                <span v-else>Przetwarzanie…</span>
              </button>
              <button type="button"
                      class="items-center mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                      @click="close" ref="cancelButtonRef">Zamknij</button>
            </div>

          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, nextTick } from 'vue';
import { db } from "@/firebase/gfbconf.js";
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ChartBarSquareIcon, DocumentDuplicateIcon, TableCellsIcon } from '@heroicons/vue/24/outline';
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSX from 'xlsx';
import moment from 'moment';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const DISCOVERY_BATCH = 300;      // paczka do odkrywania okresów
const COUNT_PAGE_SIZE = 1000;     // strona do zliczania dokumentów (doc length)
const COUNT_CONCURRENCY = 3;      // ile okresów liczymy równolegle
const FETCH_PAGE = 500;           // paczka do generowania dokumentu

export default {
  components: {
    ChartBarSquareIcon, DocumentDuplicateIcon, TableCellsIcon,
    Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot,
  },
  props: ["modalData"],
  setup() {
    const open = ref(true);
    const sentinel = ref(null);
    return { open, sentinel };
  },
  data() {
    return {
      periods: [],           // [{ key, meta:{count,loading}, firstUnix, selected, parsed }]
      isLoading: false,
      canLoadMore: false,
      lastDoc: null,
      preventSpam: false,
      countQueue: [],
      inflight: new Set(),
      counted: new Set(),
      countEpoch: 0,
      io: null,
    };
  },
  computed: {
    hasAnySelected() { return this.periods.some(p => p.selected); },
    selectedPeriodKeys() { return this.periods.filter(p => p.selected).map(p => p.key); },
  },
  methods: {
    parsePeriod(str) {
      if (typeof str !== 'string') return null;
      const [a, b] = str.split(' - ');
      if (!a || !b) return null;
      const parseOne = (s) => {
        const [d, m, y] = s.split('-').map(n => parseInt(n, 10));
        if (!d || !m || !y) return null;
        const pad = n => String(n).padStart(2, '0');
        return { d, m, y, iso: `${y}-${pad(m)}-${pad(d)}` };
      };
      const p1 = parseOne(a), p2 = parseOne(b);
      if (!p1 || !p2) return null;
      return {
        start: Date.UTC(p1.y, p1.m - 1, p1.d, 0, 0, 0),
        end:   Date.UTC(p2.y, p2.m - 1, p2.d, 23, 59, 59),
        startISO: p1.iso, endISO: p2.iso,
      };
    },

    /* ------- Odkrywanie okresów -------- */
    async loadFirstPage() {
      this.periods = [];
      this.lastDoc = null;
      this.countQueue = [];
      this.inflight.clear();
      this.counted.clear();
      this.countEpoch++;
      this.detachObserver();
      await this.loadMore();
      this.attachObserver();
    },
    async loadMore(batch = DISCOVERY_BATCH) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        let q = db.collection("Invoices").orderBy("created.unix", "desc").limit(batch);
        if (this.lastDoc) q = q.startAfter(this.lastDoc);
        const snap = await q.get();
        const docs = snap.docs;
        if (docs.length) this.lastDoc = docs[docs.length - 1];

        const byKey = new Map(this.periods.map(p => [p.key, p]));
        const newly = [];

        for (const d of docs) {
          const inv = d.data();
          const period = inv?.period;
          if (!period) continue;
          if (!byKey.has(period)) {
            const obj = {
              key: period,
              meta: { count: null, loading: true },
              firstUnix: inv?.created?.unix ?? 0,
              selected: false,
              parsed: this.parsePeriod(period),
            };
            byKey.set(period, obj);
            newly.push(period);
          } else {
            const obj = byKey.get(period);
            const unix = inv?.created?.unix ?? 0;
            if (unix > obj.firstUnix) obj.firstUnix = unix;
          }
        }

        const arr = Array.from(byKey.values());
        arr.sort((A, B) => {
          const a = A.parsed, b = B.parsed;
          if (a && b) {
            if (b.start !== a.start) return b.start - a.start;
            if (b.end !== a.end) return b.end - a.end;
            return B.key.localeCompare(A.key);
          }
          if (A.firstUnix !== B.firstUnix) return B.firstUnix - A.firstUnix;
          return B.key.localeCompare(A.key);
        });

        this.periods = arr;
        this.canLoadMore = docs.length === batch;

        this.enqueueCounts(newly);
        this.kickCounters();

        await nextTick();
        this.attachObserver();
      } catch (e) {
        console.error(e);
        alert('Nie udało się załadować okresów.');
      } finally {
        this.isLoading = false;
      }
    },

    /* ------- Kolejka liczników (doc length) ------- */
    enqueueCounts(keys) {
      for (const k of keys) {
        if (!this.counted.has(k)) {
          const row = this.periods.find(p => p.key === k);
          if (row) { row.meta.loading = true; row.meta.count = null; }
          this.countQueue.push(k);
        }
      }
    },
    async kickCounters() {
      const epoch = this.countEpoch;
      while (this.inflight.size < COUNT_CONCURRENCY && this.countQueue.length) {
        const key = this.countQueue.shift();
        if (!key || this.inflight.has(key) || this.counted.has(key)) continue;
        this.inflight.add(key);

        this.countByPaging(key)
          .then(cnt => {
            if (epoch !== this.countEpoch) return;
            this.applyCount(key, cnt);
          })
          .catch(e => {
            if (epoch !== this.countEpoch) return;
            console.warn('countByPaging fail', key, e);
            this.applyCount(key, null);
          })
          .finally(() => {
            this.inflight.delete(key);
            if (epoch !== this.countEpoch) return;
            if (this.countQueue.length) this.kickCounters();
          });
      }
    },
    async countByPaging(period, pageSize = COUNT_PAGE_SIZE) {
      let total = 0;
      let base = db.collection("Invoices").where("period", "==", period).orderBy("created.unix", "desc").limit(pageSize);
      let snap = await base.get();
      total += snap.size;
      while (snap.size === pageSize) {
        const last = snap.docs[snap.docs.length - 1];
        snap = await db.collection("Invoices")
          .where("period", "==", period)
          .orderBy("created.unix", "desc")
          .startAfter(last)
          .limit(pageSize)
          .get();
        total += snap.size;
      }
      return total;
    },
    applyCount(key, count) {
      const row = this.periods.find(p => p.key === key);
      if (!row) return;
      row.meta.loading = false;
      row.meta.count = (typeof count === 'number') ? count : null;
      this.counted.add(key);
    },

    /* ------- Infinite loading ------- */
    attachObserver() {
      if (!this.$refs.sentinel || !('IntersectionObserver' in window)) return;
      if (this.io) this.io.disconnect();
      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting && this.canLoadMore && !this.isLoading) {
            this.loadMore();
          }
        }
      }, { root: null, rootMargin: '0px', threshold: 0.1 });
      this.io.observe(this.$refs.sentinel);
    },
    detachObserver() { if (this.io) { this.io.disconnect(); this.io = null; } },

    /* ------- Paged fetch do generowania ------- */
    async *fetchInvoicesForPeriod(period, pageSize = FETCH_PAGE) {
      let q = db.collection("Invoices").where("period", "==", period).orderBy("created.unix", "desc").limit(pageSize);
      let snap = await q.get(); yield snap;
      while (!snap.empty && snap.size === pageSize) {
        const last = snap.docs[snap.docs.length - 1];
        snap = await db.collection("Invoices").where("period", "==", period).orderBy("created.unix", "desc").startAfter(last).limit(pageSize).get();
        yield snap;
      }
    },

    /* ------- Wspólna agregacja: pobiera wszystkie faktury dla wybranych okresów ------- */
    async collectSelectedInvoices() {
      const included = this.selectedPeriodKeys;
      const list = [];
      for (const p of included) {
        for await (const snap of this.fetchInvoicesForPeriod(p, FETCH_PAGE)) {
          snap.forEach(doc => list.push(doc.data()));
        }
      }
      return list;
    },

    /* ------- Pomocnicze: liczenie sum per stawka VAT z transakcji ------- */
    computeVatBreakdown(invoice) {
      const breakdown = {};
      const txs = Array.isArray(invoice?.transactions) ? invoice.transactions : [];
      for (const t of txs) {
        const rate = Number(t?.vatRate);
        const key = Number.isFinite(rate) ? rate : 0;
        const net = Number(t?.netValue) || 0;
        const vat = Number(t?.vatValue) || 0;
        const gross = Number(t?.grossValue) || 0;
        if (!breakdown[key]) breakdown[key] = { net: 0, vat: 0, gross: 0 };
        breakdown[key].net += net;
        breakdown[key].vat += vat;
        breakdown[key].gross += gross;
      }
      for (const k of Object.keys(breakdown)) {
        breakdown[k].net = Math.round(breakdown[k].net * 100) / 100;
        breakdown[k].vat = Math.round(breakdown[k].vat * 100) / 100;
        breakdown[k].gross = Math.round(breakdown[k].gross * 100) / 100;
      }
      return breakdown;
    },
    collectAllVatRates(invoices) {
      const rates = new Set();
      for (const inv of invoices) {
        const txs = Array.isArray(inv?.transactions) ? inv.transactions : [];
        for (const t of txs) {
          const r = Number(t?.vatRate);
          if (Number.isFinite(r)) rates.add(r);
        }
      }
      return Array.from(rates).sort((a, b) => b - a); // sort desc: 23, 8, 5, 0
    },

    /* ------- Generowanie XLSX (dynamiczne kolumny per stawka VAT) ------- */
    /* ------- Pomocnicze: lista wpłat do kolumny „Płatności" (tylko XLSX) ------- */
    // Każda wpłata z okna „Płatności" jest zapisana w faktura.deposits jako { amount, date }
    // (date = unix, początek dnia). Sortujemy chronologicznie, bo kolejność w tablicy to
    // kolejność wpisywania, a nie kolejność wpłat. Format daty jak w oknie płatności.
    formatDepositsForExport(invoice) {
      const deposits = Array.isArray(invoice?.deposits) ? invoice.deposits : [];
      return deposits
        .filter(d => Number.isFinite(Number(d?.amount)) && Number.isFinite(Number(d?.date)))
        .slice()
        .sort((a, b) => Number(a.date) - Number(b.date))
        .map(d => `${moment.unix(Number(d.date)).format('DD.MM.YYYY')} = ${Number(d.amount).toFixed(2).replace('.', ',')} zł`)
        .join('; ');
    },

    async generateXLSXStatement() {
      if (!this.hasAnySelected) {
        alert('Aby wygenerować zestawienie, musisz wybrać okres rozliczeniowy');
        return;
      }
      this.preventSpam = true;
      try {
        const invoices = await this.collectSelectedInvoices();
        if (!invoices.length) {
          alert('Brak danych w wybranych okresach.');
          return;
        }
        const vatRates = this.collectAllVatRates(invoices);
        const pickedPeriods = this.selectedPeriodKeys;

        // Header
        const header = [
          'Lp.',
          'Faktura',
          'Nr KSeF',
          'Firma',
          'NIP nabywcy',
          'Okres',
          'Data wystawienia',
          'Wartość netto',
          'Wartość VAT',
          'Wartość brutto',
        ];
        for (const r of vatRates) {
          header.push(`Wartość netto (VAT${r}%)`);
          header.push(`Wartość VAT (VAT${r}%)`);
          header.push(`Wartość brutto (VAT${r}%)`);
        }
        header.push('Data płatności', 'Opłacona', 'Opłacona kwota', 'Płatności');

        const rows = [header];
        let sumNet = 0, sumVat = 0, sumGross = 0, sumPaid = 0;
        const sumPerRate = {};
        for (const r of vatRates) sumPerRate[r] = { net: 0, vat: 0, gross: 0 };

        invoices.forEach((inv, i) => {
          const net = Number(inv?.netValueOfInvoice) || 0;
          const vat = Number(inv?.vatValueOfInvoice) || 0;
          const gross = Number(inv?.valueOfInvoice) || 0;
          const paid = Number(inv?.paidAmount) || 0;
          sumNet += net; sumVat += vat; sumGross += gross; sumPaid += paid;

          const breakdown = this.computeVatBreakdown(inv);
          const row = [
            i + 1,
            inv?.invoiceName || '',
            inv?.ksef?.ksefNumber || '',
            inv?.payerName || '',
            inv?.payerNIP || '',
            inv?.period || '',
            inv?.invoiceDeliveryDate || '',
            Math.round(net * 100) / 100,
            Math.round(vat * 100) / 100,
            Math.round(gross * 100) / 100,
          ];
          for (const r of vatRates) {
            const b = breakdown[r] || { net: 0, vat: 0, gross: 0 };
            row.push(b.net, b.vat, b.gross);
            sumPerRate[r].net += b.net;
            sumPerRate[r].vat += b.vat;
            sumPerRate[r].gross += b.gross;
          }
          const isPaid = (!inv?.paid && paid > 0) ? 'Częściowo' : (inv?.paid ? 'Tak' : 'Nie');
          row.push(inv?.paymentDate || '', isPaid, Math.round(paid * 100) / 100, this.formatDepositsForExport(inv));
          rows.push(row);
        });

        // Podsumowanie
        const totalsRow = [
          '',
          'SUMA',
          '',
          '',
          '',
          '',
          '',
          Math.round(sumNet * 100) / 100,
          Math.round(sumVat * 100) / 100,
          Math.round(sumGross * 100) / 100,
        ];
        for (const r of vatRates) {
          totalsRow.push(
            Math.round(sumPerRate[r].net * 100) / 100,
            Math.round(sumPerRate[r].vat * 100) / 100,
            Math.round(sumPerRate[r].gross * 100) / 100,
          );
        }
        totalsRow.push('', '', Math.round(sumPaid * 100) / 100, '');
        rows.push([]);
        rows.push(totalsRow);

        const ws = XLSX.utils.aoa_to_sheet(rows);
        const numericCols = new Set();
        // numeric formatting for all money columns (indices >= 7, except Data płatności/Opłacona column offsets)
        const perRateCols = vatRates.length * 3;
        const paymentDateColIdx = 10 + perRateCols;
        const isPaidColIdx = paymentDateColIdx + 1;
        const paidAmountColIdx = paymentDateColIdx + 2;
        const paymentsColIdx = paymentDateColIdx + 3;
        for (let c = 7; c < header.length; c++) {
          if (c === paymentDateColIdx || c === isPaidColIdx || c === paymentsColIdx) continue;
          numericCols.add(c);
        }
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
          for (const c of numericCols) {
            const addr = XLSX.utils.encode_cell({ r: R, c });
            if (ws[addr] && typeof ws[addr].v === 'number') {
              ws[addr].t = 'n';
              ws[addr].z = '0.00';
            }
          }
        }
        // column widths
        ws['!cols'] = header.map((h, idx) => {
          if (idx === 0) return { wch: 5 };
          if (idx === 1) return { wch: 24 };
          if (idx === 2) return { wch: 36 };
          if (idx === 3) return { wch: 24 };
          if (idx === 4) return { wch: 14 };
          if (idx === 5) return { wch: 26 };
          if (idx === 6) return { wch: 14 };
          if (idx === paymentsColIdx) return { wch: 60 };
          return { wch: 18 };
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Zestawienie');
        const safe = pickedPeriods.join('_').replaceAll('/', '-').replaceAll('\\', '-').replaceAll(':', '-').replaceAll(' - ', '--');
        XLSX.writeFile(wb, `Zestawienie_faktur_${safe}.xlsx`);
        setTimeout(() => this.close(), 500);
      } catch (e) {
        console.error(e);
        alert('Wystąpił błąd podczas generowania zestawienia XLSX.');
      } finally {
        this.preventSpam = false;
      }
    },

    /* ------- Generowanie PDF ------- */
    async generateStatement() {
      if (!this.hasAnySelected) {
        alert("Aby wygenerować zestawienie do pliku PDF, musisz wybrać okres rozliczeniowy");
        return;
      }
      this.preventSpam = true;
      try {
        const invoicesToGenerate = await this.collectSelectedInvoices();
        if (!invoicesToGenerate.length) {
          alert('Brak danych w wybranych okresach.');
          return;
        }
        const pickedPeriods = this.selectedPeriodKeys;
        let allNetValue = 0, allVatValue = 0, allGrossValue = 0, paidAmount = 0;

        for (const invoice of invoicesToGenerate) {
          invoice.grossValue = Number(invoice.valueOfInvoice) || 0;
          invoice.vatValue = Number(invoice.vatValueOfInvoice) || 0;
          invoice.netValue = Number(invoice.netValueOfInvoice) || 0;
          paidAmount += Number(invoice.paidAmount) || 0;
          paidAmount = Math.round(paidAmount * 100) / 100;
          allNetValue += invoice.netValue;
          allVatValue += invoice.vatValue;
          allGrossValue += invoice.grossValue;
        }

        const invoicesTable = [
          [
            { text: 'Lp.', border: [false, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Faktura', fillColor: '#eaf2f5', alignment: 'center', border: [true, true, true, true], margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Firma', fillColor: '#eaf2f5', alignment: 'center', border: [true, true, true, true], margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Okres', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Wartość netto', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Wartość vat', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Wartość brutto', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Data płatności', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 5, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Opłacona', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
            { text: 'Opłacona kwota', border: [true, true, true, true], alignment: 'center', fillColor: '#eaf2f5', margin: [-9, 10, -9, 5], textTransform: 'uppercase', fontSize: 8 },
          ],
        ];

        invoicesToGenerate.forEach((crntInvoice, t) => {
          invoicesTable.push([
            { text: `${t + 1}`, border: [false, true, true, true], margin: [-9, 5, -5, 5], alignment: 'right', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.invoiceName || ''}`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.payerName || ''}`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.period || ''}`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.netValue.toFixed(2)} PLN`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.vatValue.toFixed(2)} PLN`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.grossValue.toFixed(2)} PLN`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${crntInvoice.paymentDate || ''}`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${!crntInvoice.paid && Number(crntInvoice.paidAmount) > 0 ? 'Częściowo' : crntInvoice.paid ? 'Tak' : 'Nie'}`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
            { border: [true, true, true, true], text: `${(Number(crntInvoice.paidAmount) || 0).toFixed(2)} PLN`, fillColor: '#f5f5f5', margin: [-9, 5, -9, 5], alignment: 'center', fontSize: 8 },
          ]);
        });

        const docDefinition = {
          pageSize: 'A4',
          pageOrientation: 'landscape',
          content: [
            { columns: [[ { text: 'Zestawienie faktur', color: '#333333', width: '*', fontSize: 16, bold: true, alignment: 'center', margin: [0, 10, 0, 10] } ]] },
            { columns: [ { text: 'Wybrane okresy', color: '#aaaaab', bold: true, fontSize: 12, alignment: 'left', margin: [0, 15, 0, 2] } ] },
            { columns: [ { text: `${pickedPeriods.toString().replaceAll(',', '\n')}`, bold: true, fontSize: 10, color: '#333333', alignment: 'left' } ] },
            '\n',
            {
              layout: {
                defaultBorder: false,
                hLineWidth: () => 1, vLineWidth: () => 1,
                hLineColor: (i) => (i === 0 || i === 1) ? '#bfdde8' : '#eaeaea',
                vLineColor: () => '#eaeaea',
                hLineStyle: () => null,
                paddingLeft: () => 10, paddingRight: () => 10,
                paddingTop: () => 2, paddingBottom: () => 2,
                fillColor: () => '#fff',
              },
              table: { headerRows: 1, dontBreakRows: true, widths: [1, 70, '*', 80, 45, 45, 45, 30, 25, 45], body: invoicesTable },
            },
            { stack: [
              {
                layout: {
                  defaultBorder: false,
                  hLineWidth: () => 1, vLineWidth: () => 1,
                  hLineColor: () => '#eaeaea', vLineColor: () => '#eaeaea',
                  hLineStyle: () => null,
                  paddingLeft: () => 10, paddingRight: () => 10,
                  paddingTop: () => 3, paddingBottom: () => 3,
                  fillColor: () => '#fff',
                },
                table: {
                  headerRows: 1, dontBreakRows: true, widths: ['66%', '34%'],
                  body: [
                    [ { text: 'Wartość netto', border: [false, true, false, true], alignment: 'right', margin: [0, 5, 0, 5] }, { border: [false, true, false, true], text: `${allNetValue.toFixed(2)} PLN`, alignment: 'right', fillColor: '#f5f5f5', margin: [0, 5, 0, 5] } ],
                    [ { text: 'Wartość VAT', border: [false, false, false, true], alignment: 'right', margin: [0, 5, 0, 5] }, { text: `${allVatValue.toFixed(2)} PLN`, border: [false, false, false, true], fillColor: '#f5f5f5', alignment: 'right', margin: [0, 5, 0, 5] } ],
                    [ { text: 'Wartość brutto', border: [false, false, false, true], alignment: 'right', margin: [0, 5, 0, 5] }, { text: `${allGrossValue.toFixed(2)} PLN`, border: [false, false, false, true], fillColor: '#f5f5f5', alignment: 'right', margin: [0, 5, 0, 5] } ],
                    [ { text: 'Pozostało do opłacenia', bold: true, fontSize: 16, alignment: 'right', border: [false, false, false, true], margin: [0, 5, 0, 5] }, { text: `${(Math.round((allGrossValue - paidAmount) * 100) / 100).toFixed(2)} PLN`, bold: true, fontSize: 16, alignment: 'right', border: [false, false, false, true], fillColor: '#f5f5f5', margin: [0, 5, 0, 5] } ],
                  ],
                },
              },
            ], unbreakable: true },
          ],
          styles: {},
          defaultStyle: { columnGap: 20 },
        };

        pdfMake.createPdf(docDefinition).download(`Zestawienie_faktur.pdf`);
        setTimeout(() => this.close(), 500);
      } catch (e) {
        console.error(e);
        alert('Wystąpił błąd podczas generowania zestawienia PDF.');
      } finally {
        this.preventSpam = false;
      }
    },

    /* ------- Generowanie CSV ------- */
    async generateCSVStatement() {
      if (!this.hasAnySelected) {
        alert("Aby wygenerować zestawienie do pliku CSV, musisz wybrać okres rozliczeniowy");
        return;
      }
      this.preventSpam = true;
      try {
        const invoicesToGenerate = await this.collectSelectedInvoices();
        if (!invoicesToGenerate.length) {
          alert('Brak danych w wybranych okresach.');
          return;
        }

        const statementHeaders = {
          index: 'Lp.',
          invoiceName: 'Faktura',
          companyName: 'Firma',
          timePeriod: 'Okres',
          netValue: 'Wartość netto',
          vatValue: 'Wartość vat',
          gross: 'Wartość brutto',
          paymentDate: 'Data płatności',
          isPaid: 'Opłacona',
          paidValue: 'Opłacona kwota',
        };
        const statementCSV = [statementHeaders];

        invoicesToGenerate.forEach((crntInvoice, t) => {
          const net = Number(crntInvoice.netValueOfInvoice) || 0;
          const vat = Number(crntInvoice.vatValueOfInvoice) || 0;
          const gross = Number(crntInvoice.valueOfInvoice) || 0;
          const paid = Number(crntInvoice.paidAmount) || 0;
          statementCSV.push({
            index: `${t + 1}`,
            invoiceName: `${crntInvoice.invoiceName || ''}`,
            companyName: `${crntInvoice.payerName || ''}`,
            timePeriod: `${crntInvoice.period || ''}`,
            netValue: `${net.toFixed(2)} PLN`,
            vatValue: `${vat.toFixed(2)} PLN`,
            gross: `${gross.toFixed(2)} PLN`,
            paymentDate: `${crntInvoice.paymentDate || ''}`,
            isPaid: `${!crntInvoice.paid && paid > 0 ? 'Częściowo' : crntInvoice.paid ? 'Tak' : 'Nie'}`,
            paidValue: `${paid.toFixed(2)} PLN`,
          });
        });

        const csvMaker = (data) => {
          const csvRows = [];
          for (let i = 0; i < data.length; i++) {
            csvRows.push(Object.values(data[i]).join(';'));
          }
          return csvRows.join('\n');
        };
        const download = (data) => {
          const blob = new Blob([data], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.setAttribute('href', url);
          a.setAttribute('download', `EXCEL-CSV_Zestawienie_faktur.csv`);
          a.click();
        };
        download(csvMaker(statementCSV));
        setTimeout(() => this.close(), 500);
      } catch (e) {
        console.error(e);
        alert('Wystąpił błąd podczas generowania zestawienia CSV.');
      } finally {
        this.preventSpam = false;
      }
    },

    emitClose() {
      this.$emit("close");
    },
    close() {
      this.preventSpam = false;
      this.detachObserver();
      this.emitClose();
      this.open = false;
    },
  },
  mounted() {
    this.loadFirstPage();
  },
  beforeUnmount() { this.detachObserver(); },
};
</script>

<style scoped></style>
